import { IAccordianListItem, IAccordionList } from 'components/common/SideTray/side-tray.interface';
import { ActionIconsForAccordion } from 'core/constants/common';
import { DataSourceSelectionType, DimensionStatus, SideTrayType } from 'core/constants/report';
import { FilterOperatorTypes, JoinPrefix, LogicalExprTypes } from 'core/constants/report-expressions';
import { JoinItem } from 'core/models/report-builder/report-builder';
import {
  IExpression,
  newColumnExpr,
  newComparisonExpr,
  newAndExpr,
  isComp,
  isAnd,
  isOr,
  isLogical,
} from 'core/models/report-expressions';
import { FieldEntities } from 'core/utils/report.util';
import {
  IDataSourceFieldType, IDataSourceMapping, IDataSourceResponse, IQueryInfo,
  ISelectedQueryInfo,
} from 'redux-v2/report-builder/report-builder-store.state';
import { SubQueryPanelType } from 'core/utils/report-builder.util';
import isEqual from 'lodash/isEqual';
import { JoinBlockValues } from './DataSourceEdit/data-source-component';
import { isValidArrayAndNotEmpty } from '../../Report/ReportSidebar/common/helpers';
import { ROOT_QUERY_KEY } from '../../Report/ReportSidebar/common/constants';
import { getDefaultFieldFromJoinCondition } from './join-conditions.util';

export const createTableList = (
  dataSources: IDataSourceResponse,
  selectedTables: Map<number, JoinBlockValues>,
  tableType: DataSourceSelectionType,
  joinBlockIndex: number,
  joinArray: JoinItem[],
  primaryDataSource: string,
  querySource: string,
  queryList: IQueryInfo[],
  selectedQuery: string,
): IAccordionList[] => {
  const geEntityActionIcon = () => {
    switch (tableType) {
      case DataSourceSelectionType.querySource:
        if (querySource) {
          return ActionIconsForAccordion.SWITCH;
        }
        return ActionIconsForAccordion.ADD;
      case DataSourceSelectionType.lhsTable:
        return (joinArray[joinBlockIndex]?.Primary) !== null ? ActionIconsForAccordion.SWITCH : ActionIconsForAccordion.ADD;
      case DataSourceSelectionType.rhsTable:
        return joinArray[joinBlockIndex]?.With !== null ? ActionIconsForAccordion.SWITCH : ActionIconsForAccordion.ADD;
      default: return ActionIconsForAccordion.ADD;
    }
  };
  const selectedQueryExtraData = isValidArrayAndNotEmpty(queryList) && queryList.find((query) => query.Id === selectedQuery);
  const queriesWithRootAsParent = isValidArrayAndNotEmpty(queryList) ? queryList.filter((query) => query.ParentQueryId === ROOT_QUERY_KEY).map((query) => query.Id) : [];
  const groupedData: Record<string, IAccordianListItem[]> = Object.keys(dataSources)
    .filter((tableKey) => {
      if (tableType === DataSourceSelectionType.querySource) {
        return tableKey !== querySource;
      }
      if (tableType === DataSourceSelectionType.lhsTable && joinBlockIndex === 0) {
        if (dataSources[tableKey].IsQuery) {
          return false;
        }
        return !selectedTables.get(joinBlockIndex)?.selected?.includes(tableKey);
      }
      if (tableType === DataSourceSelectionType.lhsTable && joinBlockIndex > 0) {
        const availableTables = new Set<string>();
        if (primaryDataSource) availableTables.add(primaryDataSource);
        joinArray.slice(0, joinBlockIndex).forEach((priorJoin) => {
          if (!priorJoin) return;
          if (priorJoin.Primary) availableTables.add(priorJoin.Primary);
          if (priorJoin.With) availableTables.add(priorJoin.With);
          if (priorJoin.On) {
            const { conditions } = extractJoinConditions(normalizeJoinConditions(priorJoin.On));
            if (isValidArrayAndNotEmpty(conditions)) {
              const priorPart: any = conditions[0]?.Part;
              const priorLhsTable = priorPart?.LHS?.Part?.TableNameOverride?.replace(JoinPrefix, '');
              if (priorLhsTable) availableTables.add(priorLhsTable);
            }
          }
        });
        const selectedTablesForBlock = selectedTables.get(joinBlockIndex)?.selected || [];
        return availableTables.has(tableKey) && !selectedTablesForBlock.includes(tableKey);
      }
      if (dataSources[tableKey]?.IsQuery && tableType === DataSourceSelectionType.rhsTable) {
        return false;
      }
      const allSelectedTables = Array.from(selectedTables.values()).flatMap((joinBlockItem) => joinBlockItem.selected);
      if (dataSources[tableKey].IsQuery && !selectedQueryExtraData?.IsDraft) {
        const linkedQueryIds = queryList.find((query) => query.Id === tableKey)?.LinkedQueryIds;
        if (selectedQuery === ROOT_QUERY_KEY) {
          return queriesWithRootAsParent.includes(tableKey) && !allSelectedTables.includes(tableKey);
        }
        return linkedQueryIds?.includes(tableKey);
      }
      return !allSelectedTables.includes(tableKey);
    })
    .reduce((acc: Record<string, IAccordianListItem[]>, tableKey) => {
      const tableData = dataSources[tableKey];
      const entity = tableData.Entity;
      const newItem: IAccordianListItem = {
        name: tableData.DisplayName,
        uniqueKey: tableKey,
        toolTipContent: tableData.DisplayName,
        isMasked: false,
        originalIndex: 0,
        isItemDisable: false,
        iconItemToolTipText: tableData.DisplayName,
        renderIcon: geEntityActionIcon(),
        data: tableData,
      };
      if (!acc[entity]) {
        acc[entity] = [];
      }
      acc[entity].push(newItem);
      return acc;
    }, {});
  const accordions = Object.keys(groupedData).map((entityKey) => ({
    headerName: entityKey, // Use Entity as header
    headerIcon: FieldEntities[entityKey],
    accordValues: groupedData[entityKey],
    emptyStateText: `No ${entityKey} available`,
    allItemsAddedMessage: `All ${entityKey} selected`,
    groupId: entityKey,
    onAccordChange: () => {},
    openAccordSectionOnInitialLoad: false,
    isCustomExp: false,
  }));
  return accordions;
};

export const createFieldsList = (dataSources: IDataSourceResponse, entity: string, selectedFieldsMap: Map<number, JoinBlockValues>, joinBlockIndex: number, currentFieldSchemaName?: string): IAccordionList[] => Object.keys(dataSources)
  .filter((tableKey) => tableKey === entity)
  .map((tableKey) => {
    const tableData = dataSources[tableKey];
    const addFields:any[] = isValidArrayAndNotEmpty(tableData?.AdditionalFields) ? tableData?.AdditionalFields : [];
    const accordValues: IAccordianListItem[] = addFields
      .filter((field:IDataSourceFieldType) => {
        const selectedFields = selectedFieldsMap.get(joinBlockIndex)?.selected || [];
        if (!isValidArrayAndNotEmpty(selectedFields)) return true;
        return !selectedFields.includes(field.SchemaName);
      })
      .map((field:IDataSourceFieldType, index:number) => ({
        name: field.DisplayName,
        uniqueKey: `${tableKey}-${field.SchemaName}`,
        toolTipContent: field.DisplayName,
        isMasked: false,
        originalIndex: index,
        isItemDisable: false,
        iconItemToolTipText: field.DisplayName,
        renderIcon: currentFieldSchemaName ? ActionIconsForAccordion.SWITCH : ActionIconsForAccordion.ADD,
        data: field,
        openAccordSectionOnInitialLoad: false,
      }));
    return {
      headerName: tableData.DisplayName,
      accordValues,
      emptyStateText: 'No additional fields available',
      allItemsAddedMessage: 'All additional fields added',
      groupId: tableData.Entity,
      headerIcon: FieldEntities[tableData.Entity],
      onAccordChange: () => {},
      openAccordSectionOnInitialLoad: false,
      isCustomExp: false,
    };
  });

export const getSidetrayAccordianList = (
  sideTrayContent: DataSourceSelectionType,
  dataSources: IDataSourceResponse,
  joinsArray: JoinItem[],
  selectedRHSTables: Map<number, JoinBlockValues>,
  defaultNamespace: string,
  selectedFields: Map<number, JoinBlockValues>,
  joinBlockIndex: number,
  selectedLHSTables: Map<number, JoinBlockValues>,
  querySource: string,
  queryList: IQueryInfo[],
  selectedQuery: string,
  conditionIndex?: number,
) => {
  let accordionList: IAccordionList[] = [];
  const dataSourcesKeys = Object.keys(dataSources);
  let preDefinedDataSources = {};
  dataSourcesKeys.forEach((dataSourceValue) => {
    if (!dataSources[dataSourceValue].IsQuery) {
      preDefinedDataSources = {
        ...preDefinedDataSources, [dataSourceValue]: dataSources[dataSourceValue],
      };
    }
  });
  if (sideTrayContent === DataSourceSelectionType.querySource) {
    accordionList = createTableList(preDefinedDataSources, new Map(), DataSourceSelectionType.querySource, joinBlockIndex, joinsArray, defaultNamespace, querySource, queryList, selectedQuery);
    return accordionList;
  }
  if (isValidArrayAndNotEmpty(joinsArray)) {
    const joinItem = joinsArray[joinBlockIndex];
    const normalizedExpression = normalizeJoinConditions(joinItem?.On);
    const { conditions } = extractJoinConditions(normalizedExpression);
    const idx = conditionIndex ?? 0;
    const condition = conditions[idx];
    const part: any = condition?.Part;
    switch (sideTrayContent) {
      case DataSourceSelectionType.lhsTable: {
        accordionList = createTableList(dataSources, selectedLHSTables, DataSourceSelectionType.lhsTable, joinBlockIndex, joinsArray, defaultNamespace, querySource, queryList, selectedQuery);
        break;
      }
      case DataSourceSelectionType.rhsTable: {
        accordionList = createTableList(dataSources, selectedRHSTables, DataSourceSelectionType.rhsTable, joinBlockIndex, joinsArray, defaultNamespace, querySource, queryList, selectedQuery);
        break;
      }
      case DataSourceSelectionType.lhsField: {
        const lhsTable = (part?.LHS?.Part?.TableNameOverride?.replace(JoinPrefix, '') || null) || (joinBlockIndex > 0 ? joinsArray[joinBlockIndex - 1]?.With : null) || joinItem?.Primary;
        const currentFieldSchemaName = getDefaultFieldFromJoinCondition(joinItem, DataSourceSelectionType.lhsField, conditionIndex);
        if (!lhsTable && joinBlockIndex === 0) {
          accordionList = createFieldsList(dataSources, defaultNamespace, selectedFields, joinBlockIndex, currentFieldSchemaName);
        } else if (lhsTable) {
          accordionList = createFieldsList(dataSources, lhsTable, selectedFields, joinBlockIndex, currentFieldSchemaName);
        }
        break;
      }
      case DataSourceSelectionType.rhsField: {
        const currentFieldSchemaName = getDefaultFieldFromJoinCondition(joinItem, DataSourceSelectionType.rhsField, conditionIndex);
        accordionList = createFieldsList(dataSources, joinItem?.With, selectedFields, joinBlockIndex, currentFieldSchemaName);
        break;
      }
      default: break;
    }
  }
  return accordionList;
};

export const getDefaultJoinCriteria = (
  primaryDataSource:string,
  withDataSource: string,
  dataSources: IDataSourceResponse,
  primaryNamespace?: string,
  joinCriteria? : IExpression,
  queryList?: Array<IQueryInfo>,
  selectedQueryData?: ISelectedQueryInfo,
): IExpression => {
  if (!primaryDataSource || !withDataSource) return null;
  const primaryDataSourceDefaultField = dataSources[primaryDataSource].DefaultField;
  const withDataSourceDefaultField = dataSources[withDataSource].DefaultField;
  let lhsColumn;
  let rhsColumn;
  if (joinCriteria && selectedQueryData) {
    const part: any = joinCriteria?.Part;
    if (queryList?.some((query) => query.Id === primaryDataSource)) {
      if (isFieldPartOfQuery(joinCriteria, DataSourceSelectionType.lhsTable, selectedQueryData)) {
        const newLhsColumn = newColumnExpr(primaryDataSourceDefaultField.DisplayName, primaryDataSourceDefaultField.SchemaName, primaryDataSourceDefaultField.DataType, primaryDataSource === primaryNamespace ? primaryDataSource : `${JoinPrefix}${primaryDataSource}`);
        if (isEqual(part?.LHS, newLhsColumn)) {
          lhsColumn = part?.LHS;
        }
      }
    } else {
      lhsColumn = part?.LHS;
    }

    if (queryList?.some((query) => query.Id === withDataSource)) {
      if (isFieldPartOfQuery(joinCriteria, DataSourceSelectionType.rhsTable, selectedQueryData)) {
        const newRhsColumn = newColumnExpr(withDataSourceDefaultField.DisplayName, withDataSourceDefaultField.SchemaName, withDataSourceDefaultField.DataType, withDataSource === primaryNamespace ? withDataSource : `${JoinPrefix}${withDataSource}`);
        if (isEqual(part?.RHS, newRhsColumn)) {
          rhsColumn = part?.RHS;
        }
      }
    } else {
      rhsColumn = part?.RHS;
    }
  }
  const lhs = lhsColumn || newColumnExpr(primaryDataSourceDefaultField.DisplayName, primaryDataSourceDefaultField.SchemaName, primaryDataSourceDefaultField.DataType, primaryDataSource === primaryNamespace ? primaryDataSource : `${JoinPrefix}${primaryDataSource}`);
  const rhs = rhsColumn || newColumnExpr(withDataSourceDefaultField.DisplayName, withDataSourceDefaultField.SchemaName, withDataSourceDefaultField.DataType, withDataSource === primaryNamespace ? withDataSource : `${JoinPrefix}${withDataSource}`);
  const compExpr = newComparisonExpr(lhs, rhs, FilterOperatorTypes.EQ);
  return newAndExpr([compExpr]);
};

export const isSingleComparison = (expression: IExpression | null | undefined): boolean => !!expression && isComp(expression);

export const normalizeJoinConditions = (expression: IExpression | null | undefined): IExpression | null => {
  if (!expression) return null;
  if (isSingleComparison(expression)) return newAndExpr([expression]);
  if (isLogical(expression) && (isAnd(expression) || isOr(expression))) return expression;
  return newAndExpr([expression]);
};

export const extractJoinConditions = (
  expression: IExpression | null | undefined,
): { conditions: IExpression[], logicalOp: LogicalExprTypes } => {
  if (!expression) return { conditions: [], logicalOp: LogicalExprTypes.And };
  if (isSingleComparison(expression)) return { conditions: [expression], logicalOp: LogicalExprTypes.And };
  if (isAnd(expression) || isOr(expression)) {
    const conditions = expression.Part.Conditions.filter((cond) => isComp(cond));
    return { conditions, logicalOp: isAnd(expression) ? LogicalExprTypes.And : LogicalExprTypes.Or };
  }
  return { conditions: [], logicalOp: LogicalExprTypes.And };
};

const isFieldPartOfQuery = (
  expression: IExpression,
  dataSourceSelectionType: DataSourceSelectionType,
  selectedQueryData: ISelectedQueryInfo,
) => {
  const part: any = expression?.Part;
  const appliedDimensions = selectedQueryData.updatedInfo?.AppliedDimensions
    ?.filter((dimension) => dimension.Applied === DimensionStatus.Applied)
    ?.map((dimension) => dimension.BuilderConfig?.SchemaName);

  switch (dataSourceSelectionType) {
    case DataSourceSelectionType.lhsTable: {
      const lhsColumn = part?.LHS?.Part?.Column;
      if (appliedDimensions?.includes(lhsColumn?.SchemaName)) return true;
      break;
    }
    case DataSourceSelectionType.rhsTable: {
      const rhsColumn = part?.RHS?.Part?.Column;
      if (appliedDimensions?.includes(rhsColumn?.SchemaName)) return true;
      break;
    }
    default: break;
  }
  return false;
};

export const getDefaultBreadcrumbData = () => [
  {
    label: 'Main Report', isActive: false, queryId: ROOT_QUERY_KEY, type: SubQueryPanelType.parent,
  },
];

/**
 * Generates a display string of data source names based on the provided mapping and data sources
 * Used to show the connected/joined data sources in the UI
 *
 * @param dataSourceMapping - Object containing mapping information for queries including joins and primary namespace
 * @param queryId - Unique identifier for the query whose data source names need to be retrieved
 * @param dataSources - Object containing information about all available data sources including their display names
 * @returns A string representing either a single data source name or concatenated names of joined data sources
 *
 */
export const getDataSourceNames = (
  dataSourceMapping: IDataSourceMapping,
  queryId: string,
  dataSources: IDataSourceResponse,
): string => {
  const joinsList = dataSourceMapping[queryId]?.Joins;
  if (!isValidArrayAndNotEmpty(joinsList)) {
    if (dataSourceMapping[queryId]?.PrimaryNamespace) {
      return dataSources[dataSourceMapping[queryId]?.PrimaryNamespace]?.DisplayName;
    }
    return '';
  }
  const joinBlock = joinsList[dataSourceMapping[queryId].JoinBlockIndexBeingEdited] || {} as JoinItem;
  if (!(joinBlock?.Primary || joinBlock?.With)) {
    return dataSources[dataSourceMapping[queryId]?.PrimaryNamespace]?.DisplayName;
  }
  const primaryTable = dataSources[joinBlock?.Primary]?.DisplayName || dataSources[dataSourceMapping[queryId].PrimaryNamespace]?.DisplayName;
  const withTable = dataSources[joinBlock.With]?.DisplayName || '';

  return withTable ? `${primaryTable} + ${withTable}` : primaryTable;
};

export const getQueryName = (queryId: string, queryList: IQueryInfo[]) => queryList.find((query) => query.Id === queryId)?.Name || '';
export const getPanelSource = (type: DataSourceSelectionType) => {
  switch (type) {
    case DataSourceSelectionType.lhsTable:
    case DataSourceSelectionType.rhsTable: return SideTrayType.DataSourceEntity;
    case DataSourceSelectionType.lhsField:
    case DataSourceSelectionType.rhsField: return SideTrayType.DataSourceField;
    default: return SideTrayType.None;
  }
};
